import { useState, useEffect, useRef } from 'react';
import { GlassPanel } from '@/components/GlassPanel';
import { Brain, Cpu, Zap, Shield } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

const METRIC_ICONS = [Brain, Cpu, Zap, Shield];
const METRIC_KEYS = ['eq', 'tech', 'agility', 'brand'];
const METRIC_SCORES = [91, 88, 89, 94];
const METRIC_COLORS = ['#C87533', '#4CC9F0', '#F5A623', '#2DD4BF'];

const MetricCard = ({ metric, isActive, onClick, methodLabel }) => {
  const barRef = useRef(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setAnimated(true);
      },
      { threshold: 0.3 }
    );
    if (barRef.current) observer.observe(barRef.current);
    return () => observer.disconnect();
  }, []);

  const Icon = metric.icon;

  return (
    <button
      onClick={onClick}
      className={`text-left p-5 sm:p-6 rounded-2xl border transition-all duration-300 w-full ${
        isActive
          ? 'border-[#C87533]/40 bg-white/[0.08] backdrop-blur-[14px] shadow-[0_18px_60px_rgba(0,0,0,0.45)]'
          : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
      }`}
      data-testid={`selection-metric-${metric.key}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${metric.color}15` }}>
          <Icon className="w-5 h-5" style={{ color: metric.color }} />
        </div>
        <span className="kpi-number text-2xl font-bold" style={{ color: metric.color }}>
          {animated ? metric.score : 0}
        </span>
      </div>
      <h3 className="font-display text-sm font-semibold text-white mb-1">{metric.label}</h3>

      {/* Progress bar */}
      <div ref={barRef} className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-3">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{
            width: animated ? `${metric.score}%` : '0%',
            backgroundColor: metric.color
          }}
        />
      </div>

      {/* Method (shown when active) */}
      {isActive && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <p className="text-xs text-white/60 leading-relaxed mb-2">{metric.description}</p>
          <p className="text-xs font-mono text-white/40">
            <span className="text-[#C87533]">{methodLabel}:</span> {metric.method}
          </p>
        </div>
      )}
    </button>
  );
};

export const SelectionSection = () => {
  const [activeMetric, setActiveMetric] = useState('eq');
  const { lang } = useLanguage();
  const tx = t(lang).selection;

  const METRICS = tx.metrics.map((m, i) => ({
    key: METRIC_KEYS[i],
    label: m.label,
    icon: METRIC_ICONS[i],
    score: METRIC_SCORES[i],
    color: METRIC_COLORS[i],
    description: m.description,
    method: m.method,
  }));

  return (
    <section
      id="selection"
      data-testid="selection-section"
      className="scroll-section min-h-screen flex items-center px-4"
    >
      <div className="section-content max-w-[1200px] mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#C87533]" />
          <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
        </div>

        <div className="mb-10">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4 text-white">
            {tx.headline}
          </h2>
          <p className="text-sm sm:text-base text-white/65 max-w-2xl leading-relaxed">
            {tx.body}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {METRICS.map((metric) => (
            <MetricCard
              key={metric.key}
              metric={metric}
              isActive={activeMetric === metric.key}
              onClick={() => setActiveMetric(metric.key)}
              methodLabel={tx.methodLabel}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
